import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { ExternalLink, Download, MapPin, Music, Users } from 'lucide-react'

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? '/api').replace(/\/$/, '')
const NUM = new Intl.NumberFormat('en-US')
const fmt = (n: number | null | undefined) =>
  typeof n === 'number' && Number.isFinite(n) ? NUM.format(n) : '—'

type EpkVariant =
  | 'concert' | 'festival' | 'corporate' | 'private_party'
  | 'charity_gala' | 'commercial_tv_film' | 'general'

interface PublicEpk {
  artist: { id: string; name: string; slug: string; image: string | null }
  variant: EpkVariant
  variantConfig: {
    label: string
    headline: string
    opener: string
    statPriority: string[]
    cta: string
    accent: 'emerald' | 'amber' | 'sky' | 'fuchsia' | 'rose' | 'indigo' | 'zinc'
  }
  data: {
    spotifyFollowers: number | null
    spotifyMonthlyListeners: number | null
    spotifyPopularity: number | null
    spotifyGenres: string[]
    spotifyImage: string | null
    spotifyExternalUrl: string | null
    spotifyTopTrack: string | null
    instagramFollowers: number | null
    youtubeSubscribers: number | null
    tiktokFollowers: number | null
    topMarkets: Array<{ country: string; pct: number }>
    topCities: Array<{ city: string; country: string }>
    relatedArtists: string[]
    appleMusicUrl: string | null
    periodLabel: string
    snapshotAsOf: string
    preliminary: boolean
  }
  link: { label: string | null; createdAt: string; expiresAt: string | null }
  customHtmlDocument?: string | null
}

const ACCENT_BG: Record<PublicEpk['variantConfig']['accent'], string> = {
  emerald: 'bg-emerald-700',
  amber:   'bg-amber-700',
  sky:     'bg-sky-700',
  fuchsia: 'bg-fuchsia-700',
  rose:    'bg-rose-700',
  indigo:  'bg-indigo-800',
  zinc:    'bg-zinc-800',
}

export function EpkPublicPage() {
  const { slug, token } = useParams<{ slug: string; token: string }>()
  const [epk, setEpk] = useState<PublicEpk | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!slug || !token) return
    let cancelled = false
    fetch(`${API_BASE_URL}/public/epk/${slug}/${token}`)
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}))
          throw new Error(body.error?.message ?? `Failed to load EPK (${res.status})`)
        }
        return res.json()
      })
      .then((json) => {
        if (!cancelled) {
          setEpk(json.data)
          setLoading(false)
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.message)
          setLoading(false)
        }
      })
    return () => {
      cancelled = true
    }
  }, [slug, token])

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-zinc-50 text-zinc-600">
        Loading…
      </div>
    )
  }
  if (error || !epk) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-zinc-50 px-6 text-center">
        <div>
          <div className="text-2xl font-semibold text-zinc-900">EPK unavailable</div>
          <p className="mt-2 text-zinc-600">{error ?? 'This link is no longer active.'}</p>
        </div>
      </div>
    )
  }

  const accent = ACCENT_BG[epk.variantConfig.accent]
  const downloadUrl = `${API_BASE_URL}/public/epk/${slug}/${token}/download.docx`

  if (epk.customHtmlDocument) {
    return (
      <div className="min-h-screen bg-white">
        <div className="border-b border-zinc-200 bg-zinc-50 px-4 py-2 text-right">
          <a
            href={downloadUrl}
            className="inline-flex items-center gap-2 rounded-md bg-zinc-900 px-3 py-1.5 text-xs font-medium text-white"
          >
            <Download className="h-3.5 w-3.5" />
            Download .docx
          </a>
        </div>
        <iframe
          title={`${epk.artist.name} EPK`}
          srcDoc={epk.customHtmlDocument}
          className="h-[calc(100vh-3rem)] w-full border-0"
          sandbox=""
        />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-zinc-50 text-zinc-900">
      {/* Hero */}
      <header className={`${accent} text-white`}>
        <div className="mx-auto max-w-4xl px-6 py-12 md:py-16">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] opacity-80">
            <span>{epk.variantConfig.label} • Electronic Press Kit</span>
          </div>
          <h1 className="mt-3 text-4xl font-bold leading-tight md:text-5xl">
            {epk.artist.name}
          </h1>
          <p className="mt-3 max-w-2xl text-lg opacity-90">
            {epk.variantConfig.headline}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <a
              href={downloadUrl}
              className="inline-flex items-center gap-2 rounded-md bg-white px-4 py-2 text-sm font-medium text-zinc-900 hover:bg-zinc-100"
            >
              <Download className="h-4 w-4" />
              Download as .docx
            </a>
            {epk.data.spotifyExternalUrl && (
              <a
                href={epk.data.spotifyExternalUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-md border border-white/30 px-4 py-2 text-sm font-medium text-white hover:bg-white/10"
              >
                <Music className="h-4 w-4" />
                Listen on Spotify
                <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-6 py-10">
        {/* Opener */}
        <section className="mb-10 text-lg leading-relaxed text-zinc-700">
          {epk.variantConfig.opener}
        </section>

        {/* Stats grid */}
        <section className="mb-10">
          <h2 className="mb-4 text-xs uppercase tracking-wider text-zinc-500">
            Audience snapshot — {epk.data.periodLabel}
          </h2>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            {epk.data.spotifyMonthlyListeners != null && (
              <StatCard label="Monthly listeners" value={fmt(epk.data.spotifyMonthlyListeners)} sub="Spotify" />
            )}
            {epk.data.spotifyFollowers != null && (
              <StatCard label="Spotify followers" value={fmt(epk.data.spotifyFollowers)} />
            )}
            {epk.data.instagramFollowers != null && (
              <StatCard label="Instagram" value={fmt(epk.data.instagramFollowers)} sub="followers" />
            )}
            {epk.data.youtubeSubscribers != null && (
              <StatCard label="YouTube" value={fmt(epk.data.youtubeSubscribers)} sub="subscribers" />
            )}
            {epk.data.tiktokFollowers != null && (
              <StatCard label="TikTok" value={fmt(epk.data.tiktokFollowers)} sub="followers" />
            )}
            {epk.data.spotifyPopularity != null && (
              <StatCard label="Popularity" value={`${epk.data.spotifyPopularity}/100`} sub="Spotify score" />
            )}
          </div>
        </section>

        {/* Genres */}
        {epk.data.spotifyGenres.length > 0 && (
          <section className="mb-10">
            <h2 className="mb-3 text-xs uppercase tracking-wider text-zinc-500">Genres</h2>
            <div className="flex flex-wrap gap-2">
              {epk.data.spotifyGenres.map((g) => (
                <span key={g} className="rounded-full bg-white px-3 py-1 text-sm text-zinc-700 ring-1 ring-zinc-200">
                  {g}
                </span>
              ))}
            </div>
          </section>
        )}

        {/* Top markets */}
        {epk.data.topMarkets.length > 0 && (
          <section className="mb-10">
            <h2 className="mb-3 flex items-center gap-1.5 text-xs uppercase tracking-wider text-zinc-500">
              <MapPin className="h-3 w-3" /> Top markets
            </h2>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {epk.data.topMarkets.slice(0, 8).map((m) => (
                <div key={m.country} className="flex items-center justify-between rounded-md bg-white px-3 py-2 ring-1 ring-zinc-200">
                  <span className="font-medium">{m.country}</span>
                  <span className="text-sm text-zinc-500">{(m.pct * 100).toFixed(1)}%</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Related artists */}
        {epk.data.relatedArtists.length > 0 && (
          <section className="mb-10">
            <h2 className="mb-3 flex items-center gap-1.5 text-xs uppercase tracking-wider text-zinc-500">
              <Users className="h-3 w-3" /> For fans of
            </h2>
            <div className="flex flex-wrap gap-2">
              {epk.data.relatedArtists.slice(0, 12).map((a) => (
                <span key={a} className="rounded-md bg-white px-3 py-1.5 text-sm text-zinc-700 ring-1 ring-zinc-200">
                  {a}
                </span>
              ))}
            </div>
          </section>
        )}

        {/* CTA */}
        <section className="mt-12 rounded-lg bg-white p-6 ring-1 ring-zinc-200">
          <h2 className="text-base font-semibold text-zinc-900">Booking enquiries</h2>
          <p className="mt-2 text-zinc-700">{epk.variantConfig.cta}</p>
        </section>

        {/* Footer */}
        <footer className="mt-12 text-center text-xs text-zinc-400">
          Data as of {epk.data.snapshotAsOf.slice(0, 10)}
          {epk.data.preliminary && ' • Preliminary — refreshing shortly'}
        </footer>
      </main>
    </div>
  )
}

function StatCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-lg bg-white p-4 ring-1 ring-zinc-200">
      <div className="text-xs uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="mt-1 text-2xl font-semibold text-zinc-900">{value}</div>
      {sub && <div className="mt-0.5 text-xs text-zinc-500">{sub}</div>}
    </div>
  )
}
